import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getUserAutomationData, saveUserAutomationStatus, saveUserAutomationCustomizations, updateUserAutomationRequest } from '../services/userAutomationService';
import { useCompanyId } from '../hooks/useCompanyId';
import { Zap, X, Trash2, AlertCircle, Pencil, Check, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

// Full emoji picker (lazy so it doesn't bloat the initial bundle).
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

// Next version in the x.y scheme: bump the minor by 1, rolling 1.9 → 2.0.
const nextVersion = (current) => {
  const m = String(current || '1.0').trim().match(/^(\d+)(?:\.(\d+))?/);
  let major = m ? parseInt(m[1], 10) : 1;
  let minor = m && m[2] != null ? parseInt(m[2], 10) : 0;
  minor += 1;
  if (minor > 9) {
    major += 1;
    minor = 0;
  }
  return `${major}.${minor}`;
};

// Statuses an internal user can set directly from the "Update Status" picker.
const STATUS_OPTIONS = [
  { value: 'off', label: 'Off', color: 'bg-red-500' },
  { value: 'requested', label: 'Requested', color: 'bg-yellow-500' },
  { value: 'needs-approval', label: 'Needs Approval', color: 'bg-amber-500' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-blue-500' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500' },
  { value: 'on', label: 'On', color: 'bg-green-500' },
  { value: 'hidden', label: 'Hidden', color: 'bg-purple-500' },
];

// Renders as a routed page by default; pass automationId + onClose to embed it
// inside a modal (hides the back button and closes instead of navigating).
// overrideUserId/overrideCompanyId let an internal viewer (CRM Portal View)
// open + edit a specific client account's automation (status, connections)
// instead of their own.
const AutomationOverview = ({ automationId, onClose, overrideUserId = null, overrideCompanyId = null }) => {
  const { id: routeId } = useParams();
  const id = automationId || routeId;
  const isModal = Boolean(onClose);
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId } = useCompanyId();
  const companyId = overrideCompanyId || resolvedCompanyId;
  // The uid whose automation state (status, connections, requests) is read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;
  // Internal (CRM Portal View) usage — an override id is only ever set when an
  // internal user views a client. Internal users set the status directly and
  // can edit the per-client version; the client-facing "Request On" flow is
  // portal-only.
  const isInternal = Boolean(overrideUserId);

  const handleBack = () => {
    if (onClose) onClose();
    else navigate('/portal/technology/automation');
  };

  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableSoftwares, setAvailableSoftwares] = useState([]);
  const [softwareTypes, setSoftwareTypes] = useState([]);
  const [userTechStack, setUserTechStack] = useState({});
  const [automationStatus, setAutomationStatus] = useState('off');
  const [saving, setSaving] = useState(false);
  const [showConnectionsWarning, setShowConnectionsWarning] = useState(false);
  const [softwareConnections, setSoftwareConnections] = useState({});
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedSoftwareForConnection, setSelectedSoftwareForConnection] = useState(null);
  const [connectionFields, setConnectionFields] = useState({});

  // Inline editing of the automation's name / emoji / version / description / steps.
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', emoji: '', version: '', description: '', workflowSteps: [] });
  // Emoji picker target: { kind: 'display' } or { kind: 'step', index }.
  const [emojiTarget, setEmojiTarget] = useState(null);
  // Loaded per-user/account customizations (so partial saves merge, not clobber).
  const [customizations, setCustomizations] = useState({});
  // Internal "Update Status" / "Update Version" picker modals.
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [versionPickerOpen, setVersionPickerOpen] = useState(false);
  const [versionDraft, setVersionDraft] = useState('');

  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const panelCard = `rounded-xl border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`;

  // Fetch user's tech stack and software connections
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', effectiveUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserTechStack(userData.techStack || {});
          if (userData.softwareConnections) {
            setSoftwareConnections(userData.softwareConnections);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [currentUser, effectiveUid]);

  // Fetch available softwares from technologyStack collection
  useEffect(() => {
    const fetchSoftwares = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const softwareList = [];
        const typesList = [];

        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          for (const typeDoc of typesSnapshot.docs) {
            const typeData = typeDoc.data();
            if (typeData && typeData.name) {
              typesList.push({
                id: typeDoc.id,
                name: typeData.name,
                tier: collectionName,
                description: typeData.description || '',
                connectionFields: typeData.connectionFields || [],
              });
            }

            const softwareSnapshot = await getDocs(
              collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares')
            );
            softwareSnapshot.forEach((softwareDoc) => {
              const data = softwareDoc.data();
              if (data && data.name) {
                softwareList.push({
                  id: softwareDoc.id,
                  name: data.name,
                  type: typeDoc.id,
                  typeName: typeData.name,
                  tier: collectionName,
                  description: data.description || '',
                  image: data.image || '',
                  connectionFields: data.connectionFields || [],
                });
              }
            });
          }
        }

        softwareList.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSoftwares(softwareList);
        typesList.sort((a, b) => a.name.localeCompare(b.name));
        setSoftwareTypes(typesList);
      } catch (error) {
        console.error('Error fetching software list:', error);
      }
    };
    fetchSoftwares();
  }, []);

  // Fetch automation data (template or user request) and the user's status
  useEffect(() => {
    const fetchAutomationAndUserData = async () => {
      try {
        const automationDoc = await getDoc(doc(db, 'automations', id));

        if (automationDoc.exists()) {
          // Template automation — prefer steps from the subcollection
          const data = automationDoc.data();
          let steps = [];
          const stepsSnapshot = await getDocs(collection(db, `automations/${id}/workflowSteps`));
          if (!stepsSnapshot.empty) {
            steps = stepsSnapshot.docs
              .map((stepDoc) => ({ id: stepDoc.id, ...stepDoc.data() }))
              .sort((a, b) => (a.order || 0) - (b.order || 0));
          } else if (Array.isArray(data.workflowSteps) && data.workflowSteps.length > 0) {
            steps = [...data.workflowSteps].sort((a, b) => (a.order || 0) - (b.order || 0));
          }

          const base = { id: automationDoc.id, ...data, workflowSteps: steps };

          if (effectiveUid) {
            const userAutomationData = await getUserAutomationData(effectiveUid, id, companyId || null);
            setAutomationStatus(userAutomationData.status || 'off');
            // Overlay any saved per-user/account customizations (name, emoji,
            // description, workflow steps, version) on top of the template.
            const c = userAutomationData.customizations || {};
            setCustomizations(c);
            setAutomation({
              ...base,
              ...(c.name != null ? { name: c.name } : {}),
              ...(c.description != null ? { description: c.description } : {}),
              ...(Array.isArray(c.emojis) ? { emojis: c.emojis } : {}),
              ...(c.version ? { version: c.version } : {}),
              ...(Array.isArray(c.workflowSteps) ? { workflowSteps: c.workflowSteps } : {}),
            });
          } else {
            setAutomation(base);
          }
        } else if (currentUser) {
          // User-created automation request — prefer the company account doc
          // (the portal list reads requests from there), falling back to the
          // user doc so older per-user requests still open.
          let userRequest = null;
          if (companyId) {
            const accountDoc = await getDoc(doc(db, 'accounts', companyId));
            if (accountDoc.exists()) {
              userRequest = (accountDoc.data().userAutomationRequests || []).find((req) => req.id === id) || null;
            }
          }
          if (!userRequest && effectiveUid) {
            const userDoc = await getDoc(doc(db, 'users', effectiveUid));
            userRequest = userDoc.exists()
              ? (userDoc.data().userAutomationRequests || []).find((req) => req.id === id)
              : null;
          }

          if (userRequest) {
            setAutomation({
              id: userRequest.id,
              name: userRequest.name,
              description: userRequest.description,
              softwares: userRequest.softwares || [],
              category: userRequest.category || 'general',
              workflowSteps: userRequest.workflowSteps || [],
              emojis: userRequest.emojis || [],
              status: userRequest.status || 'requested',
              requestedBy: userRequest.requestedBy,
              company: userRequest.company,
              createdAt: userRequest.createdAt,
              isUserRequest: true,
            });
            setAutomationStatus(userRequest.status || 'requested');
          } else {
            setError('Automation not found');
          }
        } else {
          setError('Automation not found');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching automation:', error);
        setError('Failed to load automation');
        setLoading(false);
      }
    };

    if (id) fetchAutomationAndUserData();
  }, [id, currentUser, effectiveUid, companyId]);

  const getStatusColor = () => {
    switch (automationStatus) {
      case 'on': return 'bg-green-500';
      case 'requested': return 'bg-yellow-500';
      case 'needs-approval': return 'bg-amber-500';
      case 'approved': return 'bg-green-500';
      case 'in-progress': return 'bg-blue-500';
      case 'hidden': return 'bg-purple-500';
      default: return 'bg-red-500';
    }
  };

  const getStatusText = () => {
    switch (automationStatus) {
      case 'on': return 'On';
      case 'requested': return 'Requested';
      case 'needs-approval': return 'Needs Approval';
      case 'approved': return 'Approved';
      case 'in-progress': return 'In Progress';
      case 'hidden': return 'Hidden';
      default: return 'Off';
    }
  };

  // ---- Software connections ----
  const getSoftwareDisplayName = (softwareTypeId) => {
    const userSelectedSoftware = userTechStack.selectedSoftware?.find((sw) => sw.toolId === softwareTypeId);
    if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
      return userSelectedSoftware.name;
    }
    const softwareType = availableSoftwares.find((s) => s.id === softwareTypeId);
    if (softwareType) return softwareType.typeName;
    const softwareTypeInfo = softwareTypes.find((t) => t.id === softwareTypeId);
    if (softwareTypeInfo) return softwareTypeInfo.name;
    return softwareTypeId;
  };

  const getUserSoftwareLogo = (softwareTypeId) => {
    const userSelectedSoftware = userTechStack.selectedSoftware?.find((sw) => sw.toolId === softwareTypeId);
    if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
      const userSoftware = availableSoftwares.find((s) => s.name === userSelectedSoftware.name);
      if (userSoftware?.image) return userSoftware.image;
    }
    const softwareType = availableSoftwares.find((s) => s.id === softwareTypeId);
    return softwareType?.image || null;
  };

  const isSoftwareConnected = (softwareTypeId) => softwareConnections[softwareTypeId]?.connected || false;

  const areAllConnectionsEstablished = () => {
    if (!automation?.softwares || automation.softwares.length === 0) return true;
    return automation.softwares.every((softwareTypeId) => isSoftwareConnected(softwareTypeId));
  };

  const isAutomationReady = () => areAllConnectionsEstablished();

  // ---- Status changes (request on/off, approve) ----
  const handleStatusChange = async () => {
    if (!currentUser) return;
    if (automationStatus === 'off' && !isAutomationReady()) {
      setShowConnectionsWarning(true);
      return;
    }

    setSaving(true);
    try {
      let newStatus;
      if (automationStatus === 'off') newStatus = 'requested';
      else if (automationStatus === 'requested') newStatus = 'off';
      else if (automationStatus === 'needs-approval') newStatus = 'approved';
      else if (automationStatus === 'approved') newStatus = 'off';
      else newStatus = 'off';

      setAutomationStatus(newStatus);
      if (automation?.isUserRequest) {
        // Custom requests keep their status on the request entry itself —
        // that's what the cards and this popup read it from.
        await updateUserAutomationRequest(effectiveUid, id, { status: newStatus }, companyId || null);
      } else {
        await saveUserAutomationStatus(effectiveUid, id, newStatus, companyId || null);
      }
    } catch (error) {
      console.error('Error saving automation status:', error);
      setAutomationStatus(automationStatus);
    } finally {
      setSaving(false);
    }
  };

  // ---- Inline editing (name / emoji / description / steps) ----
  const handleStartEdit = () => {
    setEditForm({
      name: automation?.name || automation?.title || '',
      emoji: automation?.emojis?.[0] || '',
      version: automation?.version || '',
      description: automation?.description || '',
      workflowSteps: (automation?.workflowSteps || []).map((s) => ({
        id: s.id || `step_${Date.now()}_${Math.round((s.order || 0) * 1000)}`,
        emoji: s.emoji || '',
        title: s.title || '',
        description: s.description || '',
        order: s.order,
      })),
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEmojiTarget(null);
  };

  // Emoji picker → sets the display emoji or a step's emoji (local edit state).
  const pickEmoji = (emoji) => {
    if (!emojiTarget) return;
    if (emojiTarget.kind === 'step') {
      setEditForm((prev) => ({
        ...prev,
        workflowSteps: prev.workflowSteps.map((s, i) =>
          i === emojiTarget.index ? { ...s, emoji: emoji || '' } : s
        ),
      }));
    } else {
      setEditForm((prev) => ({ ...prev, emoji: emoji || '' }));
    }
    setEmojiTarget(null);
  };

  const updateStepField = (index, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  const addEditStep = () => {
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: [
        ...prev.workflowSteps,
        { id: `step_${Date.now()}`, emoji: '', title: '', description: '' },
      ],
    }));
  };

  const removeEditStep = (index) => {
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.filter((_, i) => i !== index),
    }));
  };

  // Persist a partial override. For a client-created request the fields live on
  // the request; for a template they're stored as per-user/account customizations
  // that overlay the shared template (never mutating the global template). Merges
  // with existing customizations so partial saves (e.g. version alone) don't clobber.
  const persistCustomizations = async (patch) => {
    if (automation?.isUserRequest) {
      await updateUserAutomationRequest(effectiveUid, id, patch, companyId || null);
    } else {
      const next = { ...customizations, ...patch };
      await saveUserAutomationCustomizations(effectiveUid, id, next, companyId || null);
      setCustomizations(next);
    }
    setAutomation((prev) => ({ ...prev, ...patch }));
  };

  const handleSaveEdits = async () => {
    if (!effectiveUid) return;
    setSaving(true);
    try {
      const cleanedSteps = editForm.workflowSteps
        .filter((s) => (s.title || '').trim())
        .map((s, i) => ({
          id: s.id || `step_${Date.now()}_${i}`,
          emoji: (s.emoji || '').trim(),
          title: s.title.trim(),
          description: (s.description || '').trim(),
          order: i + 1,
        }));
      await persistCustomizations({
        name: editForm.name.trim() || automation?.name || 'Untitled Automation',
        description: editForm.description.trim(),
        emojis: editForm.emoji.trim() ? [editForm.emoji.trim()] : [],
        workflowSteps: cleanedSteps,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving automation edits:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Internal "Update Status": set the status directly (no client-facing request flow).
  const handleStatusSet = async (newStatus) => {
    setStatusPickerOpen(false);
    if (!effectiveUid || newStatus === automationStatus) return;
    const prevStatus = automationStatus;
    setAutomationStatus(newStatus);
    setSaving(true);
    try {
      if (automation?.isUserRequest) {
        // Custom requests keep their status on the request entry itself —
        // saveUserAutomationStatus writes the userAutomations map, which the
        // request cards never read, so the change would appear not to save.
        await updateUserAutomationRequest(effectiveUid, id, { status: newStatus }, companyId || null);
      } else {
        await saveUserAutomationStatus(effectiveUid, id, newStatus, companyId || null);
      }
    } catch (error) {
      console.error('Error setting automation status:', error);
      setAutomationStatus(prevStatus);
    } finally {
      setSaving(false);
    }
  };

  // Internal "Update Version": prepopulate the next version (1.0 → 1.1 → … → 2.0).
  const openVersionPicker = () => {
    setVersionDraft(nextVersion(automation?.version || '1.0'));
    setVersionPickerOpen(true);
  };

  const handleVersionSave = async () => {
    const v = versionDraft.trim();
    setVersionPickerOpen(false);
    if (v === (automation?.version || '')) return;
    setSaving(true);
    try {
      await persistCustomizations({ version: v });
    } catch (error) {
      console.error('Error saving version:', error);
    } finally {
      setSaving(false);
    }
  };

  // ---- Connection modal ----
  const getConnectionFields = (softwareTypeId) => {
    const software = availableSoftwares.find((s) => s.id === softwareTypeId);
    const toFieldMap = (connectionFieldsArr) => {
      const fields = {};
      connectionFieldsArr.forEach((field) => {
        fields[field.fieldName] = {
          label: field.fieldName,
          description: field.fieldDescription || `Enter your ${field.fieldName.toLowerCase()}`,
          type: field.fieldType || 'text',
        };
      });
      return fields;
    };

    if (software?.connectionFields?.length > 0) return toFieldMap(software.connectionFields);

    const userSelectedSoftware = userTechStack.selectedSoftware?.find((sw) => sw.toolId === softwareTypeId);
    if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
      const userSoftware = availableSoftwares.find((s) => s.name === userSelectedSoftware.name);
      if (userSoftware?.connectionFields?.length > 0) return toFieldMap(userSoftware.connectionFields);
    }

    const softwareType = softwareTypes.find((t) => t.id === softwareTypeId);
    if (softwareType?.connectionFields?.length > 0) return toFieldMap(softwareType.connectionFields);

    return {
      apiKey: { label: 'API Key', description: 'Enter your API key for this software', type: 'password' },
      apiSecret: { label: 'API Secret', description: 'Enter your API secret', type: 'password' },
      baseUrl: { label: 'Base URL', description: 'Enter the base URL for the API', type: 'text' },
    };
  };

  const handleConnectClick = (softwareTypeId) => {
    setSelectedSoftwareForConnection(softwareTypeId);
    const existingConnection = softwareConnections[softwareTypeId];
    setConnectionFields(existingConnection?.connectionData || {});
    setShowConnectionModal(true);
  };

  const handleConnectionSave = async () => {
    if (!selectedSoftwareForConnection || !currentUser) return;
    setSaving(true);
    try {
      const userRef = doc(db, 'users', effectiveUid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const currentConnections = userDoc.data().softwareConnections || {};
        currentConnections[selectedSoftwareForConnection] = {
          connected: true,
          connectionData: connectionFields,
          connectedAt: new Date().toISOString(),
          softwareName: getSoftwareDisplayName(selectedSoftwareForConnection),
        };
        await updateDoc(userRef, {
          softwareConnections: currentConnections,
          updatedAt: new Date().toISOString(),
        });
      }

      setSoftwareConnections((prev) => ({
        ...prev,
        [selectedSoftwareForConnection]: {
          connected: true,
          connectionData: connectionFields,
          connectedAt: new Date().toISOString(),
        },
      }));
      setShowConnectionModal(false);
      setSelectedSoftwareForConnection(null);
      setConnectionFields({});
    } catch (error) {
      console.error('Error saving connection:', error);
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete (only for user-created automation requests) ----
  const handleDeleteAutomation = async () => {
    if (!currentUser || !automation?.isUserRequest) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete "${automation.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', effectiveUid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const requests = userDoc.data().userAutomationRequests || [];
        await updateDoc(userRef, {
          userAutomationRequests: requests.filter((req) => req.id !== id),
          lastUpdated: new Date().toISOString(),
        });
        handleBack();
      }
    } catch (error) {
      console.error('Error deleting automation:', error);
      alert('Error deleting automation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading automation details...</p>
        </div>
      </div>
    );
  }

  if (error || !automation) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-center text-red-500">
          <p>{error || 'Failed to load automation details'}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            {isModal ? 'Close' : 'Back to Automations'}
          </button>
        </div>
      </div>
    );
  }

  const displayEmoji = automation.emojis?.[0] || '🤖';
  const missingConnections = (automation.softwares || []).filter(
    (softwareTypeId) => !isSoftwareConnected(softwareTypeId)
  );

  return (
    <div className="relative min-h-screen p-6">
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        {isModal ? <div className="w-9" /> : <BackButton onClick={handleBack} />}
        <div className="flex-1 text-center min-w-0">
          <div className="flex items-center justify-center gap-3">
            <span className="shrink-0 w-11 h-11 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => setEmojiTarget({ kind: 'display' })}
                  className="w-9 h-9 flex items-center justify-center text-2xl leading-none rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                  title="Choose emoji"
                >
                  {editForm.emoji || '🤖'}
                </button>
              ) : (
                <span className="text-2xl leading-none">{displayEmoji}</span>
              )}
            </span>
            {isEditing ? (
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Automation name"
                className={`text-2xl font-bold text-center bg-transparent border-b border-red-300 dark:border-red-700 focus:outline-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
              />
            ) : (
              <h1 className={`text-2xl font-bold tracking-tight truncate text-gray-900 dark:text-white`}>
                {automation.name || automation.title || 'Automation Details'}
              </h1>
            )}
          </div>
          {/* Portal client status button. Internal uses the right-column
              Status panel (Update Status / Update Version) instead. */}
          {!isInternal && (
            <div className="flex items-center justify-center gap-2 mt-2">
              <button
                onClick={handleStatusChange}
                disabled={saving || isEditing}
                className={`px-4 py-1 rounded-full text-white text-sm font-medium ${getStatusColor()} hover:opacity-80 transition-opacity ${saving || isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Saving...' : getStatusText()}
              </button>
              {!automation.isUserRequest && (
                <span className={`text-xs font-medium ${subtle}`}>v{automation.version || '1.0'}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={handleSaveEdits}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Save changes"
              >
                <Check size={16} />
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleStartEdit}
              className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
              title="Edit automation"
            >
              <Pencil size={18} />
            </button>
          )}
          {automation?.isUserRequest && !isEditing && (
            <button
              onClick={handleDeleteAutomation}
              disabled={saving}
              className={`p-2 rounded-full ${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} text-white transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Delete this automation"
            >
              <Trash2 size={18} />
            </button>
          )}
          {isModal && !isEditing ? (
            <button
              onClick={onClose}
              className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
              title="Close"
            >
              <X size={22} />
            </button>
          ) : (
            <div className="w-9" />
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-5">
            {/* Description */}
            <div className={`${panelCard} p-5`}>
              <h2 className={`text-base font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Description
              </h2>
              {isEditing ? (
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  placeholder="Describe what this automation does…"
                  className={`w-full text-sm leading-relaxed rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500/40 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                />
              ) : (
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {automation.description || 'No description available'}
                </p>
              )}
            </div>

            {/* Workflow Steps — emoji + title */}
            <div className={`${panelCard} p-5`}>
              <h2 className={`text-base font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Workflow Steps
              </h2>
              {isEditing ? (
                <div className="space-y-2">
                  {editForm.workflowSteps.map((step, index) => (
                    <div
                      key={step.id || index}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}
                    >
                      <span className="shrink-0 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEmojiTarget({ kind: 'step', index })}
                        className={`shrink-0 w-10 h-9 flex items-center justify-center text-lg rounded-md border transition-colors ${darkMode ? 'bg-gray-800 border-gray-600 hover:border-red-600' : 'bg-white border-gray-300 hover:border-red-400'}`}
                        title="Choose emoji"
                      >
                        {step.emoji || '🙂'}
                      </button>
                      <input
                        type="text"
                        value={step.title}
                        onChange={(e) => updateStepField(index, 'title', e.target.value)}
                        placeholder="Step description"
                        className={`flex-1 min-w-0 text-sm rounded-md border px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-500/40 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                      <button
                        onClick={() => removeEditStep(index)}
                        className={`shrink-0 p-1.5 rounded-md ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                        title="Remove step"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addEditStep}
                    className={`w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${darkMode ? 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-400' : 'border-gray-300 text-gray-500 hover:border-red-500 hover:text-red-600'}`}
                  >
                    <Plus size={16} />
                    Add Step
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {automation?.workflowSteps?.map((step, index) => (
                    <div
                      key={step.id || index}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}
                    >
                      <span className="shrink-0 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
                        {index + 1}
                      </span>
                      {step.emoji && <span className="shrink-0 text-xl leading-none">{step.emoji}</span>}
                      <span className={`min-w-0 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {step.title}
                      </span>
                    </div>
                  ))}
                  {(!automation?.workflowSteps || automation.workflowSteps.length === 0) && (
                    <p className={`text-sm text-center py-6 ${subtle}`}>No workflow steps defined yet</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Software Integration */}
            <div className={`${panelCard} p-5`}>
              <h2 className={`text-base font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Software Integration
              </h2>
              <div className="space-y-2">
                {automation.softwares?.map((softwareTypeId) => {
                  const displayName = getSoftwareDisplayName(softwareTypeId);
                  const userLogo = getUserSoftwareLogo(softwareTypeId);
                  const isConnected = isSoftwareConnected(softwareTypeId);
                  return (
                    <div
                      key={softwareTypeId}
                      className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border ${
                        isConnected
                          ? darkMode
                            ? 'border-green-800 bg-green-900/20'
                            : 'border-green-200 bg-green-50'
                          : darkMode
                          ? 'border-gray-600 bg-gray-700/40'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          className={`shrink-0 h-8 w-8 rounded-md flex items-center justify-center ${
                            darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
                          }`}
                        >
                          {userLogo ? (
                            <img src={userLogo} alt={`${displayName} logo`} className="h-5 w-auto object-contain" />
                          ) : (
                            <Zap className={`h-4 w-4 ${isConnected ? 'text-green-500' : subtle}`} />
                          )}
                        </span>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {displayName}
                          </p>
                          <p className={`text-[11px] ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                            {isConnected ? 'Connected' : 'Not connected'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleConnectClick(softwareTypeId)}
                        className={`shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          isConnected
                            ? darkMode
                              ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                      >
                        {isConnected ? 'Edit' : 'Connect'}
                      </button>
                    </div>
                  );
                })}
                {(!automation.softwares || automation.softwares.length === 0) && (
                  <p className={`text-sm text-center py-6 ${subtle}`}>No software integrations required</p>
                )}
              </div>
            </div>

            {/* Internal: Status panel (Update Status / Update Version). Portal
                clients see the request on/off flow instead. */}
            {isInternal ? (
              <div className={`${panelCard} p-5`}>
                <h2 className={`text-base font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Status
                </h2>
                <div className="flex flex-col items-center gap-2 mb-4">
                  <span className={`px-4 py-1 rounded-full text-white text-sm font-medium ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                  {!automation.isUserRequest && (
                    <span className={`text-xs font-medium ${subtle}`}>Version v{automation.version || '1.0'}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setStatusPickerOpen(true)}
                    disabled={saving}
                    className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="text-base leading-none">🔄</span>
                    Update Status
                  </button>
                  {!automation.isUserRequest && (
                    <button
                      onClick={openVersionPicker}
                      disabled={saving}
                      className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-base leading-none">🏷️</span>
                      Update Version
                    </button>
                  )}
                </div>
              </div>
            ) : (
            /* Request panel */
            <div className={`${panelCard} p-5`}>
              {automationStatus === 'off' && (
                <div>
                  <button
                    onClick={handleStatusChange}
                    disabled={saving || !isAutomationReady()}
                    className={`w-full px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      saving || !isAutomationReady()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {saving ? 'Requesting...' : 'Request On'}
                  </button>
                  {!isAutomationReady() && (
                    <p className={`text-xs text-center mt-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                      Connect the software above to enable this button
                    </p>
                  )}
                </div>
              )}

              {automationStatus === 'on' && (
                <button
                  onClick={handleStatusChange}
                  disabled={saving}
                  className="w-full px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {saving ? 'Requesting...' : 'Request Off'}
                </button>
              )}

              {automationStatus === 'requested' && (
                <div className="text-center">
                  <p className={`text-sm mb-3 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                    Request pending approval
                  </p>
                  <button
                    onClick={handleStatusChange}
                    disabled={saving}
                    className="w-full px-4 py-2 text-sm font-medium bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    {saving ? 'Canceling...' : 'Cancel Request'}
                  </button>
                </div>
              )}

              {automationStatus === 'needs-approval' && (
                <div>
                  <button
                    onClick={handleStatusChange}
                    disabled={saving || !isAutomationReady()}
                    className={`w-full px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      saving || !isAutomationReady()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {saving ? 'Approving...' : 'Approve'}
                  </button>
                  {!isAutomationReady() && (
                    <p className={`text-xs text-center mt-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                      Connect the software above to enable Approve
                    </p>
                  )}
                </div>
              )}

              {automationStatus === 'approved' && (
                <div className="text-center">
                  <p className={`text-sm mb-3 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                    Approved — awaiting fulfillment
                  </p>
                  <button
                    onClick={handleStatusChange}
                    disabled={saving}
                    className="w-full px-4 py-2 text-sm font-medium bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    {saving ? 'Canceling...' : 'Cancel'}
                  </button>
                </div>
              )}

              {automationStatus === 'in-progress' && (
                <p className={`text-sm text-center ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                  Our team is setting up this automation
                </p>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Connections-needed warning modal */}
      {showConnectionsWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full`}>
            <div className="p-6 text-center">
              <AlertCircle className={`h-12 w-12 mx-auto mb-4 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
              <h2 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Connections Required
              </h2>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Connect the required software before requesting this automation.
              </p>
              {missingConnections.length > 0 && (
                <ul className={`text-sm text-left inline-block ${darkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1 mb-2`}>
                  {missingConnections.map((softwareTypeId) => (
                    <li key={softwareTypeId} className="flex items-center">
                      <span className="mr-2">•</span>
                      {getSoftwareDisplayName(softwareTypeId)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex justify-center`}>
              <button
                onClick={() => setShowConnectionsWarning(false)}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-md text-sm`}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Modal */}
      {showConnectionModal && selectedSoftwareForConnection && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-lg w-full`}>
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4`}>
              <h2 className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white text-center`}>
                Connect: {getSoftwareDisplayName(selectedSoftwareForConnection)}
              </h2>
            </div>
            <div className="p-5">
              <div className="space-y-4">
                {Object.entries(getConnectionFields(selectedSoftwareForConnection)).map(([fieldName, fieldConfig]) => (
                  <div key={fieldName}>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      {fieldConfig.label}
                    </label>
                    <p className={`text-xs mb-1.5 ${subtle}`}>{fieldConfig.description}</p>
                    <input
                      type={fieldConfig.type || 'text'}
                      value={connectionFields[fieldName] || ''}
                      onChange={(e) =>
                        setConnectionFields((prev) => ({ ...prev, [fieldName]: e.target.value }))
                      }
                      placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
                      className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md text-sm`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex justify-end space-x-3`}>
              <button
                onClick={() => {
                  setShowConnectionModal(false);
                  setSelectedSoftwareForConnection(null);
                  setConnectionFields({});
                }}
                className={`px-3 py-1.5 text-sm rounded-md ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleConnectionSave}
                disabled={saving}
                className={`px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal: Update Status picker */}
      {statusPickerOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
          onClick={() => setStatusPickerOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-sm rounded-xl shadow-2xl p-5 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
          >
            <h2 className={`text-lg font-semibold text-center mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Update Status
            </h2>
            <div className="space-y-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusSet(opt.value)}
                  disabled={saving}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                    automationStatus === opt.value
                      ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                      : darkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${opt.color}`} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{opt.label}</span>
                  </span>
                  {automationStatus === opt.value && <Check size={16} className="text-red-600" />}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setStatusPickerOpen(false)}
                className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal: Update Version picker (per-client) */}
      {versionPickerOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
          onClick={() => setVersionPickerOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-sm rounded-xl shadow-2xl p-5 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
          >
            <h2 className={`text-lg font-semibold text-center mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Update Version
            </h2>
            <p className={`text-sm text-center mb-4 ${subtle}`}>Applies to this client only.</p>
            <div className="flex items-center justify-center gap-2">
              <span className={`text-sm font-medium ${subtle}`}>v</span>
              <input
                type="text"
                value={versionDraft}
                autoFocus
                onChange={(e) => setVersionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleVersionSave();
                  if (e.key === 'Escape') setVersionPickerOpen(false);
                }}
                placeholder="1.0"
                className={`w-28 text-center px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/40 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setVersionPickerOpen(false)}
                disabled={saving}
                className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleVersionSave}
                disabled={saving}
                className={`px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emoji picker (display emoji + step emojis) */}
      {emojiTarget && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
          onClick={() => setEmojiTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-xl shadow-2xl p-5 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
          >
            <h2 className={`text-lg font-semibold text-center mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Choose an emoji
            </h2>
            <p className={`text-sm text-center mb-4 ${subtle}`}>
              {emojiTarget.kind === 'step'
                ? editForm.workflowSteps[emojiTarget.index]?.title || 'Workflow step'
                : `${editForm.name || automation?.name || 'Automation'} — display emoji`}
            </p>
            <div className="flex justify-center">
              <React.Suspense
                fallback={
                  <div className="flex justify-center items-center h-72 w-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={(emojiData) => pickEmoji(emojiData.emoji)}
                  theme={darkMode ? 'dark' : 'light'}
                  width="100%"
                  height={380}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </React.Suspense>
            </div>
            <div className="flex items-center justify-between mt-4">
              {(emojiTarget.kind === 'step'
                ? !!editForm.workflowSteps[emojiTarget.index]?.emoji
                : !!editForm.emoji) ? (
                <button
                  onClick={() => pickEmoji('')}
                  className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Remove emoji
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={() => setEmojiTarget(null)}
                className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AutomationOverview;
